import { useState, useRef, useEffect, useCallback } from 'react';

import { createPortal } from 'react-dom';

import { defineMessages, useIntl } from 'react-intl';

import { useDispatch, useSelector } from 'react-redux';

import PhotoCameraIcon from '@/material-icons/400-24px/photo_camera.svg?react';
import SettingsIcon from '@/material-icons/400-24px/settings.svg?react';
import { changeCompose } from 'mastodon/actions/compose';
import { Icon } from 'mastodon/components/icon';
import { IconButton } from 'mastodon/components/icon_button';

const messages = defineMessages({
  record:        { id: 'webcam_button.label',           defaultMessage: 'Record a video' },
  start:         { id: 'webcam_button.start_recording', defaultMessage: 'Start recording' },
  stop:          { id: 'webcam_button.stop_recording',  defaultMessage: 'Stop & attach' },
  cancel:        { id: 'webcam_button.cancel',          defaultMessage: 'Cancel' },
  camera_error:  { id: 'webcam_button.camera_error',    defaultMessage: 'Could not access camera: {error}' },
  select_camera: { id: 'webcam_button.select_camera',   defaultMessage: 'Select camera' },
  uploading_yt:  { id: 'webcam_button.uploading_youtube', defaultMessage: 'Uploading to YouTube…' },
  yt_error:      { id: 'webcam_button.youtube_error',   defaultMessage: 'YouTube upload failed: {error}' },
  connect_yt:    { id: 'webcam_button.connect_youtube', defaultMessage: 'Connect YouTube' },
  disconnect_yt: { id: 'webcam_button.disconnect_youtube', defaultMessage: 'Disconnect YouTube' },
  yt_connected:  { id: 'webcam_button.youtube_connected', defaultMessage: 'YouTube connected' },
  yt_disconnected: { id: 'webcam_button.youtube_disconnected', defaultMessage: 'YouTube not connected' },
  yt_title:      { id: 'webcam_button.youtube_title',   defaultMessage: 'Video title' },
  yt_title_placeholder: { id: 'webcam_button.youtube_title_placeholder', defaultMessage: 'Sign language video' },
});

const iconStyle = {
  height: null,
  lineHeight: '27px',
};

const getCsrfToken = (): string => {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]');
  return meta?.content ?? '';
};

export const WebcamButton: React.FC = () => {
  const intl     = useIntl();
  const dispatch = useDispatch();

  // Mirror the disabled logic from upload_button_container.js
  const isPoll           = useSelector((state: any) => state.getIn(['compose', 'poll']) !== null);
  const isUploading      = useSelector((state: any) => state.getIn(['compose', 'is_uploading']));
  const readySize        = useSelector((state: any) => state.getIn(['compose', 'media_attachments']).size ?? 0);
  const pendingSize      = useSelector((state: any) => state.getIn(['compose', 'pending_media_attachments']).size ?? 0);
  const maxAttachments   = useSelector((state: any) => state.getIn(['server', 'server', 'configuration', 'statuses', 'max_media_attachments'], 4));
  const hasVideoOrAudio  = useSelector((state: any) => state.getIn(['compose', 'media_attachments']).some((m: any) => ['video', 'audio'].includes(m.get('type'))));
  const hasQuote         = useSelector((state: any) => !!state.compose.get('quoted_status_id'));
  const composeText      = useSelector((state: any) => state.getIn(['compose', 'text'], ''));

  const disabled = isPoll || isUploading || (readySize + pendingSize) > maxAttachments - 1 || hasVideoOrAudio || hasQuote;

  const [open,         setOpen]         = useState(false);
  const [recording,    setRecording]    = useState(false);
  const [seconds,      setSeconds]      = useState(0);
  const [error,        setError]        = useState<string | null>(null);
  const [devices,      setDevices]      = useState<MediaDeviceInfo[]>([]);
  const [deviceId,     setDeviceId]     = useState<string>('');
  const [ytConnected,  setYtConnected]  = useState<boolean | null>(null);
  const [ytUploading,  setYtUploading]  = useState(false);
  const [ytError,      setYtError]      = useState<string | null>(null);
  const [ytTitle,      setYtTitle]      = useState<string>('');
  const [showSettings, setShowSettings] = useState(false);

  const videoRef    = useRef<HTMLVideoElement>(null);
  const streamRef   = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef   = useRef<Blob[]>([]);
  const timerRef    = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopStream = useCallback(() => {
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  }, []);

  const cleanup = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    stopStream();
    recorderRef.current = null;
    chunksRef.current   = [];
    setRecording(false);
    setSeconds(0);
    setError(null);
  }, [stopStream]);

  const handleClose = useCallback(() => {
    cleanup();
    setOpen(false);
    setDevices([]);
    setDeviceId('');
    setYtError(null);
    setYtUploading(false);
    setYtTitle('');
    setShowSettings(false);
  }, [cleanup]);

  const startStream = useCallback((selectedDeviceId: string) => {
    stopStream();
    setError(null);

    const videoConstraint = selectedDeviceId ? { deviceId: { exact: selectedDeviceId } } : true;

    const applyStream = (stream: MediaStream) => {
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      return navigator.mediaDevices.enumerateDevices();
    };

    const applyDevices = (allDevices: MediaDeviceInfo[]) => {
      const videoDevices = allDevices.filter(d => d.kind === 'videoinput');
      setDevices(videoDevices);
      if (!selectedDeviceId && videoDevices.length > 0) {
        const activeTrack = streamRef.current?.getVideoTracks()[0];
        const activeId    = activeTrack?.getSettings().deviceId ?? videoDevices[0].deviceId;
        setDeviceId(activeId);
      }
    };

    navigator.mediaDevices.getUserMedia({ audio: true, video: videoConstraint })
      .catch(() => navigator.mediaDevices.getUserMedia({ audio: false, video: videoConstraint }))
      .then(stream => applyStream(stream))
      .then(allDevices => applyDevices(allDevices))
      .catch(err => {
        setError((err as Error).message ?? 'Camera access denied');
      });
  }, [stopStream]);

  // Check YouTube connection status when modal opens
  useEffect(() => {
    if (!open) return;
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    setYtTitle(`${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())} video`);
    startStream('');
    fetch('/api/v1/youtube/status', { credentials: 'same-origin' })
      .then(r => r.json())
      .then(data => setYtConnected(data.connected))
      .catch(() => setYtConnected(false));
    return () => cleanup();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for YouTube OAuth popup via BroadcastChannel (primary) and postMessage (fallback)
  useEffect(() => {
    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel('youtube_oauth');
      bc.onmessage = (e) => {
        if (e.data?.type === 'youtube_connected') setYtConnected(true);
        else if (e.data?.type === 'youtube_error') setYtError(e.data.error ?? 'Connection failed');
      };
    } catch (_) {}

    const handler = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type === 'youtube_connected') setYtConnected(true);
      else if (e.data?.type === 'youtube_error') setYtError(e.data.error ?? 'Connection failed');
    };
    window.addEventListener('message', handler);

    return () => {
      bc?.close();
      window.removeEventListener('message', handler);
    };
  }, []);

  const handleDeviceChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const newDeviceId = e.target.value;
    setDeviceId(newDeviceId);
    startStream(newDeviceId);
  }, [startStream]);

  const handleConnectYouTube = useCallback(() => {
    const popup = window.open('/auth/youtube/connect', 'youtube_oauth', 'width=600,height=700,scrollbars=yes');
    if (!popup) return;

    const poll = setInterval(() => {
      if (popup.closed) {
        clearInterval(poll);
        // Popup closed — re-fetch status to pick up any connection
        fetch('/api/v1/youtube/status', { credentials: 'same-origin' })
          .then(r => r.json())
          .then(data => setYtConnected(data.connected))
          .catch(() => {});
      }
    }, 500);
  }, []);

  const handleDisconnectYouTube = useCallback(() => {
    fetch('/api/v1/youtube/disconnect', {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: { 'X-CSRF-Token': getCsrfToken() },
    })
      .then(() => setYtConnected(false))
      .catch(() => {});
  }, []);

  const uploadToYouTube = useCallback((blob: Blob, ext: string, title: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      const file = new File([blob], `vlog-${Date.now()}.${ext}`, { type: blob.type });
      formData.append('video', file);
      formData.append('title', title.trim() || new Date().toISOString().replace('T', ' ').slice(0, 19) + ' video');
      formData.append('privacy_status', 'unlisted');

      fetch('/api/v1/youtube/upload', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-CSRF-Token': getCsrfToken() },
        body: formData,
      })
        .then(r => r.json())
        .then(data => {
          if (data.url) resolve(data.url);
          else reject(new Error(data.error ?? 'Upload failed'));
        })
        .catch(reject);
    });
  }, []);

  const handleStart = useCallback(() => {
    if (!streamRef.current) return;

    const mimeType = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
      'video/mp4',
    ].find(m => MediaRecorder.isTypeSupported(m)) ?? '';

    chunksRef.current = [];
    const recorder = new MediaRecorder(
      streamRef.current,
      mimeType ? { mimeType } : undefined,
    );

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType || 'video/webm' });
      const ext  = blob.type.includes('mp4') ? 'mp4' : 'webm';

      if (ytConnected) {
        setYtUploading(true);
        setYtError(null);
        stopStream();

        uploadToYouTube(blob, ext, ytTitle)
          .then(url => {
            const separator = composeText ? ' ' : '';
            dispatch(changeCompose(composeText + separator + url));
            setYtUploading(false);
            setOpen(false);
            cleanup();
          })
          .catch(err => {
            setYtError((err as Error).message ?? 'Upload failed');
            setYtUploading(false);
          });
      } else {
        setYtError('Please connect your YouTube account in Settings to upload videos.');
        cleanup();
        setShowSettings(true);
      }
    };

    recorder.start();
    recorderRef.current = recorder;
    setRecording(true);
    timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
  }, [dispatch, cleanup, stopStream, uploadToYouTube, ytConnected, composeText, ytTitle]);

  const handleStop = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    recorderRef.current?.stop();
  }, []);

  const formatTime = (s: number) => {
    const m   = Math.floor(s / 60).toString().padStart(2, '0');
    const sec = (s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  const modal = open ? (
    <div
      className='webcam-modal-overlay'
      role='dialog'
      aria-modal
      aria-label={intl.formatMessage(messages.record)}
    >
      <div className='webcam-modal'>
        <button
          className='webcam-modal__close'
          onClick={handleClose}
          aria-label='Close'
        >
          ✕
        </button>
        {ytUploading ? (
          <div className='webcam-modal__uploading'>
            <div className='webcam-modal__uploading-spinner' />
            <p>{intl.formatMessage(messages.uploading_yt)}</p>
          </div>
        ) : (
          <>
            {error ? (
              <div className='webcam-modal__error'>
                {intl.formatMessage(messages.camera_error, { error })}
              </div>
            ) : (
              <video
                ref={videoRef}
                className='webcam-modal__preview'
                autoPlay
                playsInline
                muted
              />
            )}

            {recording && (
              <div className='webcam-modal__timer'>{formatTime(seconds)}</div>
            )}

            {ytError && (
              <div className='webcam-modal__error'>
                {intl.formatMessage(messages.yt_error, { error: ytError })}
              </div>
            )}

            {!recording && ytConnected && (
              <label className='webcam-modal__yt-title-label'>
                {intl.formatMessage(messages.yt_title)}
                <input
                  className='webcam-modal__yt-title'
                  type='text'
                  value={ytTitle}
                  onChange={e => setYtTitle(e.target.value)}
                  placeholder={intl.formatMessage(messages.yt_title_placeholder)}
                  maxLength={100}
                />
              </label>
            )}

            {showSettings ? (
              <div className='webcam-modal__settings'>
                {devices.length > 0 && (
                  <div className='webcam-modal__settings-row'>
                    <label className='webcam-modal__settings-label'>
                      {intl.formatMessage(messages.select_camera)}
                      <select
                        className='webcam-modal__device-select'
                        value={deviceId}
                        onChange={handleDeviceChange}
                        disabled={recording}
                      >
                        {devices.map(d => (
                          <option key={d.deviceId} value={d.deviceId}>
                            {d.label || `Camera ${devices.indexOf(d) + 1}`}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                )}
                <div className='webcam-modal__settings-row'>
                  <span className='webcam-modal__settings-label'>Connect Your YouTube Account</span>
                  {ytConnected === true ? (
                    <>
                      <span className='webcam-modal__yt-badge webcam-modal__yt-badge--connected'>
                        &#9654; {intl.formatMessage(messages.yt_connected)}
                      </span>
                      <button
                        className='button button--secondary webcam-modal__btn webcam-modal__btn--yt-disconnect'
                        onClick={handleDisconnectYouTube}
                      >
                        {intl.formatMessage(messages.disconnect_yt)}
                      </button>
                    </>
                  ) : ytConnected === false ? (
                    <>
                      <span className='webcam-modal__yt-badge webcam-modal__yt-badge--disconnected'>
                        {intl.formatMessage(messages.yt_disconnected)}
                      </span>
                      <button
                        className='button webcam-modal__btn webcam-modal__btn--yt-connect'
                        onClick={handleConnectYouTube}
                      >
                        {intl.formatMessage(messages.connect_yt)}
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            ) : null}

            <div className='webcam-modal__actions'>
              {!recording && (
                <button
                  className='button button--secondary webcam-modal__btn webcam-modal__btn--settings'
                  onClick={() => setShowSettings(s => !s)}
                  aria-label='Settings'
                >
                  <Icon id='settings' icon={SettingsIcon} />
                </button>
              )}

              {!recording ? (
                <button
                  className='button webcam-modal__btn webcam-modal__btn--record'
                  onClick={handleStart}
                  disabled={!!error}
                >
                  {intl.formatMessage(messages.start)}
                </button>
              ) : (
                <button
                  className='button webcam-modal__btn webcam-modal__btn--stop'
                  onClick={handleStop}
                >
                  {intl.formatMessage(messages.stop)}
                </button>
              )}

              <button
                className='button button--secondary webcam-modal__btn webcam-modal__btn--cancel'
                onClick={handleClose}
              >
                {intl.formatMessage(messages.cancel)}
              </button>
            </div>

            <div className='webcam-modal__youtube-status'>
              {ytConnected === true ? (
                <span className='webcam-modal__yt-badge webcam-modal__yt-badge--connected'>
                  &#9654; {intl.formatMessage(messages.yt_connected)}
                </span>
              ) : ytConnected === false ? (
                <span className='webcam-modal__yt-badge webcam-modal__yt-badge--disconnected'>
                  {intl.formatMessage(messages.yt_disconnected)}
                </span>
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>
  ) : null;

  return (
    <>
      <div className='compose-form__upload-button'>
        <IconButton
          icon='camera'
          iconComponent={PhotoCameraIcon}
          title={intl.formatMessage(messages.record)}
          disabled={disabled}
          onClick={() => setOpen(true)}
          className='compose-form__upload-button-icon'
          size={18}
          inverted
          style={iconStyle}
        />
      </div>

      {modal && createPortal(modal, document.body)}
    </>
  );
};
